import { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo, useCallback, useDeferredValue } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { inject, observer } from "mobx-react";
import { toJS } from "mobx";

import ButtonUI from "@UI/ButtonUI";
import CardUI from "@UI/CardUI";
import InputNumberUI from "@UI/InputNumberUI";
import SelectSuggestUI from "@UI/SelectSuggestUI";
import TooltipUI from "@UI/TooltipUI";

import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Paginator } from "primereact/paginator";

import ActivationFooter from "./ActivationFooter";
import ActivationHeader from "./ActivationHeader";
import DefaultListHeader from "./DefaultListHeader";

import "./style.scss";

const DefaultList = inject("mainStore")(
    observer(
        forwardRef(
            (
                {
                    mainStore,
                    checkbox = true,
                    apiUrl = "",
                    columns,
                    titleName,
                    keyFilterSearch,
                    titleAddButton,
                    handleRowClick = null,
                    header = false,
                    beforeGetData = () => {},
                    keysToGet = [],
                    typeOfHeader = "default",
                    typeOfFooter = "",
                    isPagination = false,
                    dataKey = "",
                },
                ref,
            ) => {
                const modified_sites_suggest = mainStore.getMainStore("modified.sites_suggest") || {};
                const filter = mainStore.getMainStore("filter");
                const paginationCompanies = mainStore.getMainStore("pagination");

                const [selectedItems, setSelectedItems] = useState(null);
                const [data, setData] = useState({ data: [], count: 0 });

                const navigation = useNavigate();
                const { firstParam } = useParams();

                const tableRef = useRef(null);
                const oldValueRef = useRef(null);

                const isTab = filter?.filters?.siteIds ? filter?.filters?.siteIds : false;
                const filterActiveKeyValue = filter?.filters?.active ?? "false";
                const currentSiteId = filter?.filters?.siteIds?.[0] || null;

                const filterStr = JSON.stringify(filter);

                const tableData = useMemo(() => data?.data || [], [data.data]);
                const countAll = data?.count || 0;

                const onCellEditInit = (options) => (oldValueRef.current = options.value);

                const resetScrollTop = useCallback(() => {
                    const tableContainer = tableRef.current?.getElement();
                    if (!tableContainer) return;

                    const scrollBody = tableContainer.querySelector(".p-virtualscroller.p-virtualscroller-inline");
                    if (scrollBody) scrollBody.scrollTop = 0;
                }, []);

                useEffect(() => {
                    beforeGetData();

                    return () => {
                        setData(false);
                        setSelectedItems(null);
                    };
                }, []);

                useEffect(() => {
                    if (!apiUrl) return;
                    resetScrollTop();

                    console.log("Фильтр изменился", toJS(filter));
                    getData(apiUrl);

                    return () => setData({ data: [], count: 0 });
                }, [filterStr, apiUrl, resetScrollTop]);

                const getData = useCallback(
                    async (url = apiUrl) => {
                        let finalFilter = { ...filter };

                        if (keysToGet.length) {
                            const projects = { ...(finalFilter.projects || {}) };
                            keysToGet.forEach((k) => {
                                projects[k] = 1;
                            });
                            finalFilter.projects = projects;
                        }

                        const resp = await mainStore.getByUrl(url, finalFilter);
                        if (resp?.success) setData(resp);
                    },
                    [apiUrl, filter, keysToGet, mainStore],
                );

                useImperativeHandle(ref, () => ({ getData }), [getData]);

                const preparedTableData = useMemo(() => {
                    if (!tableData || !tableData.length || !currentSiteId || filterActiveKeyValue !== "true") return tableData;

                    return tableData.map((row) => ({
                        ...row,
                        __groupKey: currentSiteId && row?.sorts?.siteId === currentSiteId ? "current" : "other",
                    }));
                }, [tableData, currentSiteId]);

                const handleClone = () =>
                    mainStore.setMainStore("modal", {
                        type: "copyCompany",
                        copyItems: selectedItems,
                        title: "Привязка оффера к витрине",
                        setSelectedItems,
                    });

                const confirmPause = (event) => {
                    confirmDialog({
                        target: event.currentTarget,
                        message: `Поставить на паузу офферы? `,
                        icon: "pi pi-exclamation-triangle",
                        acceptLabel: "Да",
                        rejectLabel: "Нет",
                        accept: () => {
                            handlePause("offers");
                        },
                    });
                };

                const handleUpdateMany = async ({ collection, elements, data }) => {
                    const finalElements = elements ?? selectedItems?.map((i) => i._id) ?? [];
                    if (!finalElements.length) return;

                    const obj = { collection, elements: finalElements, data };
                    const resp = await mainStore.postByUrl("/promo/update_many", obj, false, false);

                    if (resp?.success) {
                        setSelectedItems(null);
                        getData(apiUrl);
                    }
                };

                const handlePause = (collection) => {
                    handleUpdateMany({
                        collection,
                        data: { active: filter.filters.active === "true" ? false : true },
                    });
                };

                const handleDeleteMany = async (data) => {
                    const resp = await mainStore.deleteByUrl(`${apiUrl}`, { items: data });

                    if (resp && resp.success) {
                        setSelectedItems(null);
                        getData(apiUrl);
                    }
                };

                const confirmArchive = (event) => {
                    confirmDialog({
                        target: event.currentTarget,
                        message: `Архивировать офферы `,
                        icon: "pi pi-exclamation-triangle",
                        acceptLabel: "Да",
                        rejectLabel: "Нет",
                        accept: () => {
                            handleArchive();
                        },
                    });
                };

                const handleArchive = async () => {
                    const obj = {
                        collection: "offers",
                        elements: selectedItems.map((i) => i._id),
                        data: { archived: false, active: filter.filters.active === "true" ? false : true },
                    };

                    const resp = await mainStore.postByUrl("/promo/update_many", obj, false, false);
                    if (resp && resp.success) {
                        setSelectedItems(null);
                        getData(apiUrl);
                    }
                };

                const onRowClick = useCallback(
                    (e) => {
                        // если ячейка в таблице редактируемая, то останавливаем клик по row, и даем отработать редактированию
                        const target = e.originalEvent.target;
                        const isEditableCell = target.classList.contains("p-editable-column") || target.closest(".p-editable-column");
                        if (isEditableCell) return;
                        // если ячейка в таблице редактируемая, то останавливаем клик по row, и даем отработать редактированию

                        if (handleRowClick) {
                            // выполняет функцию, если она приходит из пропсов вместо дефолтного перехода ниже по navigation
                            handleRowClick(e);
                            return;
                        }

                        navigation(e.data._id);
                    },
                    [handleRowClick, navigation],
                );

                const handlePagination = (value) => mainStore.setMainStore("filter", { ...filter, ...value });

                const rowClass = useCallback(
                    (rowData) => {
                        if (firstParam !== "companies") return { list__row: true };

                        const { dateEnd, dateStart } = rowData || {};
                        if (!dateEnd || !dateStart) return { list__row: true };

                        const nowTs = Date.now();
                        const endTs = new Date(dateEnd).getTime();
                        const startTs = new Date(dateStart).getTime();

                        return {
                            dateEnd: nowTs >= endTs,
                            dateStart: nowTs < startTs,
                            list__row: true,
                        };
                    },
                    [firstParam],
                );

                const cellEditor = (options, it) => {
                    let node = null;
                    const type = it.isEdit;

                    switch (type) {
                        case "number":
                            node = <InputNumberUI value={options.value} onChange={(e) => options.editorCallback(e.value)} />;
                            break;

                        default:
                            break;
                    }

                    return node;
                };

                const onCellEditComplete = async (options) => {
                    const field = options.field;
                    const value = oldValueRef.current;
                    const newValue = options.newValue;

                    if (newValue && JSON.stringify(value) !== JSON.stringify(newValue)) {
                        switch (field) {
                            case "sorts.sort": {
                                const siteId = filter?.filters?.siteIds?.[0] || "";
                                const offerId = options?.rowData?._id || "";

                                if (siteId && offerId) {
                                    const data = [{ offerId, siteId, sort: newValue }];
                                    console.log("edit sort", data);

                                    const resp = await mainStore.postByUrl("/promo/offerbysitesortsmany", { data });
                                    if (resp.success) {
                                        mainStore.setMainStore("toast", { severity: "success", summary: "Сортировка успешно добавлена" });
                                        getData(apiUrl);
                                    }
                                }
                                break;
                            }

                            default:
                                break;
                        }
                    }
                };

                const handleRemove = async () => {
                    const toDelete = [];
                    selectedItems?.forEach((i) => i?.sorts?._id && toDelete.push({ _id: i.sorts._id }));

                    if (toDelete.length) {
                        console.log("remove sort data", toDelete);

                        const resp = await mainStore.postByUrl("/promo/offerbysitesortsmany", { type: "delete", data: toDelete });
                        if (resp.success) {
                            const siteId = filter?.filters?.siteIds?.[0] || "";
                            if (siteId) {
                                const siteName = modified_sites_suggest?.[siteId]?.name ?? "";
                                mainStore.setMainStore("toast", {
                                    severity: "success",
                                    summary: siteName ? `Оффер снят с витрины «${siteName}»` : "Оффер снят с витрины",
                                    life: 6000,
                                });
                            }
                        }
                        setSelectedItems(null);
                        getData(apiUrl);
                    } else {
                        mainStore.setMainStore("toast", { severity: "warn", summary: "Сортировка отсутствует" });
                    }
                };

                const onSelectionChange = useCallback((e) => setSelectedItems(e.value), []);

                const headerTable = (
                    <div className="list__buttons">
                        {firstParam == "companies" ? (
                            selectedItems && selectedItems.length ? (
                                <>
                                    {isTab == "65363d1ae368f2c1de818b11" || !isTab ? <ButtonUI icon="pi pi-plus" severity="help" outlined onClick={handleClone} label="Привязка оффера к витрине" /> : null}
                                    {isTab ? <ButtonUI icon="pi pi-times" severity="secondary" outlined onClick={handleRemove} label="Снять с витрины" /> : null}
                                </>
                            ) : null
                        ) : null}
                        {selectedItems && selectedItems.length ? (
                            <>
                                <ButtonUI label="Пауза" icon="pi pi-pause" severity="warning" outlined onClick={confirmPause} />
                                <ButtonUI label="Архив" icon="pi pi-inbox" severity="danger" outlined onClick={confirmArchive} />
                            </>
                        ) : null}
                    </div>
                );

                const renderHeader = () => {
                    switch (typeOfHeader) {
                        case "activation":
                            return <ActivationHeader titleName={titleName} />;

                        default:
                            return (
                                <DefaultListHeader
                                    counts={{
                                        active: data?.countActive ?? null,
                                        draft: data?.countDraft ?? null,
                                        inactive: data?.countInactive != null || data?.countArchive != null ? (data?.countInactive ?? 0) + (data?.countArchive ?? 0) : null,
                                    }}
                                    titleName={titleName}
                                    keyFilterSearch={keyFilterSearch}
                                    titleAddButton={titleAddButton}
                                    countAll={countAll}
                                />
                            );
                    }
                };

                const renderFooter = () => {
                    switch (typeOfFooter) {
                        case "activation":
                            return <ActivationFooter selectedItems={selectedItems} handleDeleteMany={handleDeleteMany} data={data} />;
                        default:
                            return null;
                    }
                };

                const tableOptions = useMemo(() => {
                    let obj = {};
                    if (filterActiveKeyValue == "true" && currentSiteId && currentSiteId !== "65363d1ae368f2c1de818b11") {
                        // не ВСЕ и не ris.promo вкладки
                        const rowGroupHeaderTemplate = (rowDataOrOptions) => {
                            const group = rowDataOrOptions?.__groupKey;
                            if (group === "current") return null;
                            if (group === "other")
                                return (
                                    <div className="list__divider">
                                        Активные офферы, не привязанные к этой витрине
                                        <i id="list-divider-info" className="pi pi-info-circle" />
                                        <TooltipUI target="#list-divider-info" position="right" content="Привязка офферов к витринам выполняется во вкладке «все»" />
                                    </div>
                                );
                            return null;
                        };
                        obj = {
                            rowGroupMode: currentSiteId ? "subheader" : null,
                            groupRowsBy: currentSiteId ? "__groupKey" : null,
                            sortMode: "single",
                            sortField: "__groupKey",
                            sortOrder: 1,
                            rowGroupHeaderTemplate,
                        };
                    } else {
                        const isUploadPage = firstParam == "uploaded-activations" ? true : false;
                        obj = {
                            scrollable: true,
                            scrollHeight: `calc(100vh - ${((isPagination || paginationCompanies) && selectedItems?.length) || isUploadPage ? 290 : isPagination || paginationCompanies || selectedItems?.length ? 210 : 140}px)`,
                            virtualScrollerOptions: { itemSize: 45 },
                        };
                    }

                    return obj;
                }, [filterActiveKeyValue, currentSiteId, isPagination, paginationCompanies, selectedItems?.length]);

                const deferredPreparedTableData = useDeferredValue(preparedTableData);

                return (
                    <div className="list">
                        <CardUI className="list__card">
                            {renderHeader()}

                            <DataTable
                                value={deferredPreparedTableData}
                                dataKey={dataKey}
                                header={header ? headerTable : null}
                                selectionMode={"checkbox"}
                                selection={selectedItems}
                                onSelectionChange={onSelectionChange}
                                onRowClick={onRowClick}
                                rows={filter && filter.rows ? filter.rows : 20}
                                emptyMessage="Ничего не найдено"
                                rowClassName={rowClass}
                                editMode="cell"
                                ref={tableRef}
                                {...tableOptions}
                            >
                                {checkbox && <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />}
                                {columns.map((it) =>
                                    it.isEdit ? (
                                        <Column
                                            key={it._id}
                                            field={it.field}
                                            header={it.header}
                                            sortable={it.sortable}
                                            style={it.style}
                                            body={it.body}
                                            editor={(options) => cellEditor(options, it)}
                                            onCellEditComplete={onCellEditComplete}
                                            onCellEditInit={onCellEditInit}
                                        />
                                    ) : (
                                        <Column key={it._id} field={it.field} header={it.header} sortable={it.sortable} style={it.style} body={it.body} />
                                    ),
                                )}
                            </DataTable>

                            {(tableData.length > 0 && isPagination) || paginationCompanies ? (
                                <div className="list__footer">
                                    <Paginator
                                        first={filter.first}
                                        rows={filter.rows}
                                        page={0}
                                        totalRecords={countAll}
                                        onPageChange={(e) => {
                                            handlePagination({ first: e.first });
                                        }}
                                    />
                                    <SelectSuggestUI
                                        value={filter.rows}
                                        onChange={(e) => {
                                            handlePagination({ rows: e.target.value, first: 0 });
                                        }}
                                        options={[
                                            { label: 20, value: 20 },
                                            { label: 50, value: 50 },
                                            { label: 100, value: 100 },
                                            { label: 1000, value: 1000 },
                                            { label: 20000000, value: "Все" },
                                        ]}
                                        optionValue="label"
                                        optionLabel="value"
                                        addClass="list__count"
                                        filter={false}
                                        showClear={false}
                                        placeholder=""
                                    />
                                    <div
                                        className="pagination__all"
                                        onClick={() =>
                                            handlePagination({
                                                rows: 20000000,
                                                first: 0,
                                            })
                                        }
                                    >
                                        Открыть все
                                    </div>
                                </div>
                            ) : null}
                        </CardUI>

                        {renderFooter()}
                    </div>
                );
            },
        ),
    ),
);
export default DefaultList;
